import { useInstallations } from '@/installation/api'
import { useLocation } from '@/location/api'
import { getShortEquipmentDetails } from '@/installation/installation'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Plus, Wrench } from 'lucide-react'
import { useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { equipmentNameFormSchema } from '@/installFlow/schemas'
import { toast } from 'sonner'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepHeader } from '@/components/installFlow/StepHeader'
import { StepFooter } from '@/components/installFlow/StepFooter'
import * as z from 'zod'
import { useInstallFlowNavigation } from '@/installFlow/useInstallFlowNavigation'
import { useStepSearch } from '@/installFlow/useStepSearch'

/**
 * Inner component that fetches and displays installation list
 * Separated to isolate the suspending useInstallations call
 */
function InstallationList({
  locationId,
  searchQuery,
  onSelect,
}: {
  locationId: number
  searchQuery: string
  onSelect: (installation: any) => void
}) {
  const { data: { results: installations } = { results: [] } } = useInstallations({ locationId })

  const filteredInstallations = useMemo(() => {
    if (!searchQuery.trim()) return installations

    const query = searchQuery.toLowerCase()
    return installations.filter(installation =>
      installation.name.toLowerCase().includes(query) ||
      (installation.serial_number?.toLowerCase().includes(query)) ||
      getShortEquipmentDetails(installation.asset).toLowerCase().includes(query)
    )
  }, [installations, searchQuery])

  return (
    <div className="space-y-4">
      {installations.length > 0 && (
        <>
          {filteredInstallations.map((installation) => {
            const hasSensorKit = !!installation.sensor_kit
            return (
              <Card
                key={installation.id}
                className={`p-4 transition-colors ${
                  hasSensorKit
                    ? 'opacity-50 cursor-not-allowed border-dashed'
                    : 'hover:border-primary cursor-pointer'
                }`}
                onClick={() => !hasSensorKit && onSelect(installation)}
                data-testid={`installation-card-${installation.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="aspect-square bg-blue-50 flex items-center justify-center p-2 rounded-lg">
                    <Wrench className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{installation.name}</h3>
                      {hasSensorKit && (
                        <Badge variant="secondary" className="text-xs">
                          Sensor Kit Installed
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {getShortEquipmentDetails(installation.asset)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {installation.sublocation}
                    </p>
                  </div>
                </div>
              </Card>
            )
          })}
        </>
      )}
    </div>
  )
}

/**
 * Header component that fetches location data
 * Separated to isolate the suspending useLocation call
 */
function InstallationStepHeader({
  locationId,
  onBack,
  searchQuery,
  onSearchChange,
}: {
  locationId: number
  onBack: () => void
  searchQuery: string
  onSearchChange: (value: string) => void
}) {
  // Fetch location from server (for display) - THIS SUSPENDS
  const { data: location } = useLocation(locationId)

  return (
    <StepHeader
      title="Select the HVAC equipment"
      description={`We'll map this Thalo sensor to equipment at ${location.name}.`}
      onBack={onBack}
      searchBar={{
        placeholder: 'Search equipment',
        value: searchQuery,
        onChange: (e) => onSearchChange(e.target.value),
      }}
    />
  )
}

/**
 * Main content for "add new equipment" form
 * Fetches location data for display
 */
function AddNewEquipmentForm({
  locationId,
  form,
  onSubmit,
}: {
  locationId: number
  form: any
  onSubmit: () => void
}) {
  // Fetch location from server (for display) - THIS SUSPENDS
  const { data: location } = useLocation(locationId)

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-2 items-center justify-center text-center">
        <div className="w-fit aspect-square rounded-lg bg-blue-100 p-4 flex justify-center items-center">
          <Wrench className="text-thalo-blueLight" size={48}/>
        </div>
        <div className="text-muted-foreground space-y-2">
          <p>
            <span>Add new equipment</span>
            {
              location && (
                <>
                  <span> to </span>
                  <span className="font-semibold">{location.name}</span>
                </>
              )
            }
          </p>
        </div>
      </div>
      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="name">
                  Equipment Name <span className="text-red-500">*</span>
                </Label>
                <FormControl>
                  <Input
                    id="name"
                    placeholder="e.g. Rooftop AC Unit 1, Main Chiller"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </div>
  )
}

export function InstallationStep() {
  const { goToStep, goToPreviousStep } = useInstallFlowNavigation()

  const search = useStepSearch('installation')
  const locationId = search.locationId

  const [searchQuery, setSearchQuery] = useState('')
  const [showForm, setShowForm] = useState(false)

  // Form pre-fills from URL params (if navigating backwards)
  // Note: installationName only exists on sublocation/details steps, not installation step
  const form = useForm<z.infer<typeof equipmentNameFormSchema>>({
    resolver: zodResolver(equipmentNameFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
    },
  })

  const handleSelectInstallation = (installation: any) => {
    const hasSensorKit = !!installation.sensor_kit
    if (hasSensorKit) {
      toast.error('Equipment Unavailable', {
        description: 'This equipment already has a sensor installed.',
      })
      return
    }

    // Navigate directly to sensor step with IDs in URL
    // No store update needed - URL is source of truth
    goToStep('link', {
      installationId: installation.id,
      sublocationId: installation.sublocation_id,
      // locationId already in URL, will be preserved
    })
  }

  const handleCreateEquipment = form.handleSubmit(async (values) => {
    goToStep('sublocation', {
      installationName: values.name,
    })
  })

  const handleBack = () => {
    goToPreviousStep()
  }

  // Guard against missing locationId
  if (!locationId) {
    return null
  }

  // Show "add new equipment" form
  if (showForm) {
    return (
      <StepFrame
        header={
          <StepHeader
            title="Add new equipment"
            onBack={() => setShowForm(false)}
          />
        }
        main={
          <AddNewEquipmentForm
            locationId={locationId}
            form={form}
            onSubmit={handleCreateEquipment}
          />
        }
        footer={
          <StepFooter
            actions={[
              <Button
                key="save"
                onClick={handleCreateEquipment}
                disabled={!form.formState.isValid}
                className="w-full"
                size="lg"
              >
                Save equipment
              </Button>,
            ]}
          />
        }
      />
    )
  }

  // Show installation list
  return (
    <StepFrame
      header={
        <InstallationStepHeader
          locationId={locationId}
          onBack={handleBack}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      }
      main={
        <InstallationList
          locationId={locationId}
          searchQuery={searchQuery}
          onSelect={handleSelectInstallation}
        />
      }
      footer={
        <StepFooter
          actions={[
            <Button
              key="add"
              onClick={() => setShowForm(true)}
              variant="outline"
              className="w-full"
              size="lg"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add new equipment
            </Button>,
          ]}
        />
      }
    />
  )
}
